import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw, AlertCircle, Plus, CheckCircle } from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer/PageContainer";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { LoadingState } from "../components/composite/LoadingState/LoadingState";
import { MessageStrip } from "../components/ui/MessageStrip/MessageStrip";
import { Badge } from "../components/ui/Badge/Badge";
import { ServiceDetailsModal } from "../components/features/ServiceDetailsModal";
import { useCustomer } from "../contexts/CustomerContext";
import { ROUTES } from "../constants/routes";
import { useServiceCatalog } from "../hooks/useServiceCatalog";
import { useCustomerServices } from "../hooks/useCustomerServices";
import customerServiceService from "../services/customerServiceService";

/**
 * ServiceCatalog Page
 *
 * Shows all available SAP services from the catalog.
 * Allows consultants to assign services to the selected customer.
 * Tracks which services are already assigned.
 */
const ServiceCatalog = () => {
  const navigate = useNavigate();
  const { selectedCustomer } = useCustomer();
  const queryClient = useQueryClient();

  // Fetch service catalog using React Query
  const {
    data: catalogResponse,
    isLoading: isCatalogLoading,
    isFetching: isCatalogFetching,
    error: catalogError,
    refetch: refetchCatalog,
  } = useServiceCatalog();

  // Fetch customer's assigned services using React Query
  const { data: customerServicesData } = useCustomerServices(
    selectedCustomer?.id,
  );

  // Extract catalog data and metadata from response
  const catalogData = catalogResponse?.data || null;
  const metadata = catalogResponse?.metadata || null;

  // Create Set of assigned service IDs for fast lookup (memoized to prevent unnecessary re-renders)
  const assignedServices = useMemo(() => {
    return new Set(customerServicesData?.map((s) => s.serviceId) || []);
  }, [customerServicesData]);

  // Combined loading state (use isFetching for refetch feedback, isLoading for initial load)
  const isLoading = isCatalogLoading && !catalogData; // Only show loading state on initial load
  const isRefreshing = isCatalogFetching && !isCatalogLoading; // Show refresh state when refetching

  // Selection state
  const [selectedServices, setSelectedServices] = useState([]);
  const [activeTab, setActiveTab] = useState("apps_and_pods");

  // Search and filters
  const [searchQuery, setSearchQuery] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState("all"); // all, assigned, available

  // Assignment state
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState(null);
  const [error, setError] = useState(null);

  // Service details modal state
  const [selectedServiceForDetails, setSelectedServiceForDetails] =
    useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  // Handle catalog errors
  useEffect(() => {
    if (catalogError) {
      setError(catalogError.message || "Failed to load service catalog data.");
    }
  }, [catalogError]);

  // Handle refresh - React Query will handle the loading/error states
  const handleRefresh = () => {
    setError(null);
    refetchCatalog();
  };

  const handleToggleService = useCallback((service) => {
    setSelectedServices((prev) => {
      const exists = prev.find((s) => s.serviceId === service.serviceId);
      if (exists) {
        // Remove from selection
        return prev.filter((s) => s.serviceId !== service.serviceId);
      } else {
        // Add to selection
        return [...prev, service];
      }
    });
  }, []);

  const handleSelectAll = useCallback(
    (category) => {
      const services = catalogData[category] || [];
      const unassignedServices = services.filter(
        (s) => !assignedServices.has(s.serviceId),
      );

      // Check if all unassigned in this category are selected
      const allSelected = unassignedServices.every((s) =>
        selectedServices.find((sel) => sel.serviceId === s.serviceId),
      );

      setSelectedServices((prev) => {
        if (allSelected) {
          // Deselect all from this category
          return prev.filter(
            (s) =>
              !unassignedServices.find((us) => us.serviceId === s.serviceId),
          );
        } else {
          // Select all unassigned from this category
          const newSelections = unassignedServices.filter(
            (s) => !prev.find((p) => p.serviceId === s.serviceId),
          );
          return [...prev, ...newSelections];
        }
      });
    },
    [catalogData, assignedServices, selectedServices],
  );

  const handleBulkAssign = async () => {
    if (selectedServices.length === 0 || !selectedCustomer) return;

    setIsAssigning(true);
    setAssignmentResult(null);

    try {
      // Prepare services for bulk assignment
      const servicesToAssign = selectedServices.map((service) => ({
        serviceId: service.serviceId,
        initialVersionId: service.id, // Use the ServiceVersion ID as initial version
      }));

      const result = await customerServiceService.bulkAssignServices(
        selectedCustomer.id,
        servicesToAssign,
      );

      setAssignmentResult(result);

      // Invalidate and refetch customer services to update the cache
      queryClient.invalidateQueries({
        queryKey: ["customerServices", selectedCustomer.id],
      });

      // Clear selection
      setSelectedServices([]);
    } catch (err) {
      console.error("Failed to assign services:", err);
      // Set error as a string, not an object
      setError(err.message || "Failed to assign services to customer.");
    } finally {
      setIsAssigning(false);
    }
  };

  const isServiceSelected = (serviceId) => {
    return selectedServices.some((s) => s.serviceId === serviceId);
  };

  const isServiceAssigned = (serviceId) => {
    return assignedServices.has(serviceId);
  };

  const handleServiceClick = (service) => {
    setSelectedServiceForDetails(service);
    setDetailsModalOpen(true);
  };

  const renderServiceTable = (category) => {
    const allServices = catalogData?.[category] || [];

    // Apply search and assignment filters
    const services = allServices.filter((service) => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        service.serviceId?.toLowerCase().includes(searchLower) ||
        service.name?.toLowerCase().includes(searchLower) ||
        service.description?.toLowerCase().includes(searchLower);

      // Assignment filter
      const isAssigned = isServiceAssigned(service.serviceId);
      const matchesAssignment =
        assignmentFilter === "all" ||
        (assignmentFilter === "assigned" && isAssigned) ||
        (assignmentFilter === "available" && !isAssigned);

      return matchesSearch && matchesAssignment;
    });

    if (services.length === 0) {
      return (
        <div className="flex items-center justify-center h-32 border rounded-lg">
          <p className="text-gray-500">
            {searchQuery || assignmentFilter !== "all"
              ? "No services match your filters."
              : "No services found in this category."}
          </p>
        </div>
      );
    }

    const selectedInCategory = services.filter((s) =>
      isServiceSelected(s.serviceId),
    ).length;
    const unassignedCount = services.filter(
      (s) => !isServiceAssigned(s.serviceId),
    ).length;
    const allSelected =
      unassignedCount > 0 && selectedInCategory === unassignedCount;

    return (
      <div className="space-y-3">
        {/* Category Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {selectedInCategory} of {unassignedCount} unassigned selected
            </span>
          </div>
          {unassignedCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSelectAll(category)}
            >
              {allSelected ? "Deselect All" : "Select All Unassigned"}
            </Button>
          )}
        </div>

        {/* Services Table */}
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {services.map((service, index) => {
                  const assigned = isServiceAssigned(service.serviceId);
                  const selected = isServiceSelected(service.serviceId);

                  return (
                    <tr
                      key={`${service.serviceId}-${index}`}
                      onClick={() => handleServiceClick(service)}
                      className={`
                        hover:bg-gray-50 transition-colors cursor-pointer
                        ${selected && !assigned ? "bg-blue-50" : ""}
                        ${assigned ? "opacity-60" : ""}
                      `}
                    >
                      <td className="px-3 py-2">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {service.name || service.serviceId}
                          </span>
                          <span className="text-xs text-gray-500">
                            {service.serviceId}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 max-w-xl">
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {service.description || "-"}
                        </p>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-center">
                        {assigned ? (
                          <Badge variant="success">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Assigned
                          </Badge>
                        ) : (
                          <Badge variant="default">Available</Badge>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 whitespace-nowrap text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {assigned ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(ROUTES.SERVICE_USAGE)}
                          >
                            View
                          </Button>
                        ) : (
                          <Button
                            variant={selected ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleToggleService(service)}
                          >
                            {selected ? "Selected" : "Select"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  if (!selectedCustomer) {
    return (
      <PageContainer
        title="Service Catalog"
        description="View and assign SAP service IDs to customers"
      >
        <Card className="p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-orange-500" />
          <h3 className="text-lg font-semibold mb-2">No Customer Selected</h3>
          <p className="text-gray-600 mb-4">
            Please select a customer to view and assign services.
          </p>
          <Button onClick={() => navigate(ROUTES.SELECT_CUSTOMER)}>
            Select Customer
          </Button>
        </Card>
      </PageContainer>
    );
  }

  // Get release version from catalog data
  const releaseVersion =
    catalogData?.apps_and_pods?.[0]?.version ||
    catalogData?.plugins?.[0]?.version ||
    catalogData?.pod2_plugins?.[0]?.version ||
    catalogData?.apis?.[0]?.version;

  return (
    <>
      <PageContainer
        title={
          <div className="flex items-center gap-3">
            <span>Service Catalog</span>
            {releaseVersion && (
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                {releaseVersion}
              </span>
            )}
          </div>
        }
        description={`Assign SAP service IDs to ${selectedCustomer.name}`}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw
                className={`w-4 h-4 flex-shrink-0 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
              <span className="font-medium text-sm leading-none">Refresh</span>
            </button>
            {selectedServices.length > 0 && (
              <button
                onClick={handleBulkAssign}
                disabled={isAssigning}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium text-sm leading-none">
                  Assign {selectedServices.length} Service
                  {selectedServices.length !== 1 ? "s" : ""}
                </span>
              </button>
            )}
          </div>
        }
      >
        {/* Assignment Result Message */}
        {assignmentResult && (
          <MessageStrip
            type={assignmentResult.summary.failed > 0 ? "warning" : "success"}
            className="mb-4"
            onClose={() => setAssignmentResult(null)}
          >
            <div>
              <p className="font-medium">
                Assigned {assignmentResult.summary.successful} of{" "}
                {assignmentResult.summary.total} services
              </p>
              {assignmentResult.summary.skipped > 0 && (
                <p className="text-sm">
                  {assignmentResult.summary.skipped} already assigned (skipped)
                </p>
              )}
              {assignmentResult.errors.length > 0 && (
                <div className="mt-2 text-sm">
                  <p className="font-medium">Errors:</p>
                  <ul className="list-disc list-inside">
                    {assignmentResult.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </MessageStrip>
        )}

        {/* Error Message */}
        {error && (
          <MessageStrip
            type="error"
            className="mb-4"
            onClose={() => setError(null)}
          >
            {error}
          </MessageStrip>
        )}

        {/* Loading State */}
        {isLoading && <LoadingState message="Loading service catalog..." />}

        {/* Content */}
        {!isLoading && catalogData && (
          <Card>
            {/* Tabs with integrated filters */}
            <div className="border-b">
              <div className="flex items-center justify-between px-4 py-2">
                <nav className="flex space-x-6" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab("apps_and_pods")}
                    className={`
                    py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                    ${
                      activeTab === "apps_and_pods"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                  >
                    Apps & PODs {"("}POD 1.0{")"} ({metadata?.apps_count || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab("plugins")}
                    className={`
                    py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                    ${
                      activeTab === "plugins"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                  >
                    POD 1.0 Plugins ({metadata?.plugins_count || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab("pod2_plugins")}
                    className={`
                    py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                    ${
                      activeTab === "pod2_plugins"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                  >
                    POD 2.0 Plugins ({metadata?.pod2_plugins_count || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab("apis")}
                    className={`
                    py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                    ${
                      activeTab === "apis"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                  >
                    APIs ({metadata?.apis_count || 0})
                  </button>
                </nav>

                {/* Compact filters in header */}
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search services..."
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                  />
                  <select
                    value={assignmentFilter}
                    onChange={(e) => setAssignmentFilter(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Services</option>
                    <option value="available">Available Only</option>
                    <option value="assigned">Assigned Only</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-4">
              {activeTab === "apps_and_pods" &&
                renderServiceTable("apps_and_pods")}
              {activeTab === "plugins" && renderServiceTable("plugins")}
              {activeTab === "pod2_plugins" &&
                renderServiceTable("pod2_plugins")}
              {activeTab === "apis" && renderServiceTable("apis")}
            </div>
          </Card>
        )}
      </PageContainer>

      {/* Service Details Modal */}
      <ServiceDetailsModal
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedServiceForDetails(null);
        }}
        service={selectedServiceForDetails}
      />
    </>
  );
};

export default ServiceCatalog;
